import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  Typography,
  Button,
  CircularProgress,
  Stack,
  IconButton,
  Fade,
} from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import Layout from './Layout';
import Logo from './Logo';
import String from './String';
import test_sound from '../../assets/audio/test_sound.mp3';
import { useSession } from '../contexts/session';
import {
  AUDIO_PRECACHE_NAME,
  MAPBOX_PRECACHE_NAME,
  TILES_PRECACHE_NAME,
  VIDEO_PRECACHE_NAME,
  IMAGES_PRECACHE_NAME,
} from '../../config/precache';
import { useExperience } from '../contexts/experience';
import { useLanguage } from '../contexts/language';

export default function AdminControls({ show, onClose, showLanguageSelector }) {
  const { user, resetSavedExperiences, logout, offline } = useSession();
  const { resetStorage } = useExperience();
  const [test, setTest] = useState(false);
  const [hardResetState, setHardResetState] = useState(null);
  const [logoutState, setLogoutState] = useState(null);
  const { language, availableLanguages, setLanguage, content } = useLanguage();

  const softReset = () => {
    window.location = '/';
  };

  const hardReset = async () => {
    if (!hardResetState) {
      setHardResetState('awaitingConfirm');
      return;
    }
    if (hardResetState === 'awaitingConfirm') {
      setHardResetState('resetting');
      resetSavedExperiences();
      resetStorage();
      await caches.delete(AUDIO_PRECACHE_NAME);
      await caches.delete(TILES_PRECACHE_NAME);
      await caches.delete(MAPBOX_PRECACHE_NAME);
      await caches.delete(VIDEO_PRECACHE_NAME);
      await caches.delete(IMAGES_PRECACHE_NAME);
      window.location = '/';
    }
  };

  const logoutUser = async () => {
    if (!logoutState) {
      setLogoutState('awaitingConfirm');
      return;
    }

    if (logoutState === 'awaitingConfirm') {
      setLogoutState('loggingOut');
      await logout();
      window.location = '/';
    }
  };

  useEffect(() => {
    setTest(false);
  }, [show]);

  return (
    <Dialog fullScreen open={show} transitionDuration={400} TransitionComponent={Fade}>
      <Layout
        framed
        top={
          <>
            <Box position="absolute" top={0} right={0} textAlign="right">
              <IconButton onClick={onClose} aria-label={content('aria_close_controls_menu')}>
                <CloseOutlined color="secondary" />
              </IconButton>
            </Box>
            <Box mt={1} textAlign="center">
              <Logo />
            </Box>
          </>
        }
        bottom={
          user ? (
            <Box textAlign="center" borderTop="1px solid rgba(0,0,0,0.2)" pt={1}>
              <Typography variant="pretitle" mb={0} fontWeight="bold">
                {user?.completeName}
              </Typography>
              <Typography variant="text" mb={2}>
                {user?.email}
              </Typography>
            </Box>
          ) : null
        }
      >
        {showLanguageSelector && (
          <Stack justifyContent="center" alignItems="center" bgcolor="transparent" spacing={1} mt={1} mb={3}>
            <Typography variant="title" mb={1}>
              <String s="language" />
            </Typography>
            <Stack direction="row" spacing={1}>
              {availableLanguages?.map((lang) => (
                <Button
                  key={lang}
                  color="secondary"
                  variant={language === lang ? 'contained' : 'outlined'}
                  aria-pressed={language === lang}
                  aria-selected={language === lang}
                  onClick={() => {
                    setLanguage(lang);
                    onClose();
                  }}
                  style={{
                    textTransform: 'uppercase',
                    fontSize: '0.9rem',
                    padding: '0.25rem 1rem',
                    width: '100%',
                  }}
                >
                  {lang}
                </Button>
              ))}
            </Stack>
          </Stack>
        )}

        <Stack alignItems="center" spacing={1} position="relative" width="90%" maxWidth="600px">
          <Typography variant="title" mb={1}>
            <String s="admin_title" />
          </Typography>
          <Button color="secondary" variant="contained" fullWidth onClick={() => setTest((s) => !s)}>
            <String s="admin_test" />
            {test && <MusicNoteIcon style={{ marginLeft: 8 }} />}
          </Button>
          <Button color="secondary" variant="contained" fullWidth onClick={softReset}>
            <String s="admin_reset_soft" />
          </Button>
          {offline === null && (
            <>
              <Button color="secondary" variant="contained" fullWidth onClick={hardReset}>
                <String s="admin_reset_hard" />
              </Button>
              <Button color="secondary" variant="contained" fullWidth onClick={logoutUser}>
                <String s="admin_logout" />
              </Button>
            </>
          )}
          <Stack width="100%" pt={2}>
            <Button color="secondary" variant="outlined" fullWidth onClick={onClose}>
              <String s="admin_close" />
            </Button>
          </Stack>
        </Stack>
      </Layout>
      {test && <audio src={test_sound} autoPlay loop style={{ display: 'none' }} />}
      <Dialog fullScreen open={!!hardResetState} onClose={() => setHardResetState(null)}>
        <Layout framed>
          <Stack spacing={3} position="relative" width="90%" maxWidth="600px">
            <Typography variant="title">
              <String s="admin_reset_hard_confirm" />
            </Typography>
            <Typography>
              <String s="admin_reset_hard_desc" />
            </Typography>
            <Stack direction="row" width="100%" spacing={2}>
              <Button variant="outlined" color="secondary" onClick={() => setHardResetState(null)} autoFocus>
                <String s="cancel" />
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={hardReset}
                endIcon={
                  hardResetState === 'resetting' && (
                    <CircularProgress style={{ marginLeft: 8, color: 'white' }} size="20px" />
                  )
                }
              >
                <String s="confirm" />
              </Button>
            </Stack>
          </Stack>
        </Layout>
      </Dialog>

      <Dialog fullScreen open={!!logoutState} onClose={() => setLogoutState(null)}>
        <Layout framed>
          <Stack spacing={3} position="relative" width="90%" maxWidth="600px">
            <Typography variant="title">
              <String s="admin_logout_confirm" />
            </Typography>
            <Typography>
              <String s="admin_logout_desc" />
            </Typography>
            <Stack direction="row" width="100%" spacing={2}>
              <Button variant="outlined" color="secondary" onClick={() => setLogoutState(null)} autoFocus>
                <String s="cancel" />
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={logoutUser}
                endIcon={
                  logoutState === 'loggingOut' && (
                    <CircularProgress style={{ marginLeft: 8, color: 'white' }} size="20px" />
                  )
                }
              >
                <String s="confirm" />
              </Button>
            </Stack>
          </Stack>
        </Layout>
      </Dialog>
    </Dialog>
  );
}
